import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../utils/api';

const BannerContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 999;
  width: 100%;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  animation: slideDown 0.3s ease-out;

  @keyframes slideDown {
    from {
      transform: translateY(-100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  &.info {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
  }

  &.warning {
    background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
    color: white;
  }

  &.success {
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    color: white;
  }

  &.error {
    background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
    color: white;
  }
`;

const BannerText = styled.div`
  font-size: 15px;
  font-weight: 500;
  text-align: center;
  line-height: 1.5;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 36px;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.55);
  }
`;

const getDismissedBannerStorageKey = () => {
  const userEmail = localStorage.getItem('userEmail') || 'anonymous';
  return `dismissedInfoBanners:${userEmail}`;
};

const getBannerDismissId = (banner) => {
  if (!banner?.id) {
    return null;
  }

  return `${banner.id}:${banner.updated_at || ''}`;
};

const getDismissedBanners = () => {
  try {
    const stored = localStorage.getItem(getDismissedBannerStorageKey());
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const InfoBanner = () => {
  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonationInfo, setImpersonationInfo] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchActiveBanner();
    syncImpersonationInfo();
  }, []);

  useEffect(() => {
    const handleAuthChange = () => {
      syncImpersonationInfo();
      fetchActiveBanner();
    };

    window.addEventListener('authStatusChanged', handleAuthChange);
    return () => window.removeEventListener('authStatusChanged', handleAuthChange);
  }, []);

  const syncImpersonationInfo = () => {
    const isImpersonating = localStorage.getItem('isImpersonating') === 'true';
    if (!isImpersonating) {
      setImpersonationInfo(null);
      return;
    }

    setImpersonationInfo({
      impersonatorEmail: localStorage.getItem('impersonatorEmail') || '',
      userEmail: localStorage.getItem('userEmail') || ''
    });
  };

  const fetchActiveBanner = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch('/api/info-banner/active', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const dismissId = getBannerDismissId(data);
        const dismissedBanners = getDismissedBanners();
        setBanner(dismissId && dismissedBanners.includes(dismissId) ? null : data);
      }
    } catch (error) {
      console.error('Ошибка загрузки баннера:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreAdmin = async () => {
    try {
      setRestoring(true);
      await authAPI.restoreAdminSession();
      navigate('/admin');
    } catch (error) {
      console.error('Ошибка восстановления админской сессии:', error);
      window.alert(error?.message || 'Не удалось вернуться в административный аккаунт');
    } finally {
      setRestoring(false);
    }
  };

  const handleDismissBanner = () => {
    const dismissId = getBannerDismissId(banner);
    if (!dismissId) {
      setBanner(null);
      return;
    }

    const dismissedBanners = getDismissedBanners();
    if (!dismissedBanners.includes(dismissId)) {
      localStorage.setItem(
        getDismissedBannerStorageKey(),
        JSON.stringify([...dismissedBanners, dismissId])
      );
    }
    setBanner(null);
  };

  const hasInfoBanner = !loading && banner && banner.is_active;
  const hasImpersonationBanner = Boolean(impersonationInfo?.impersonatorEmail || impersonationInfo?.userEmail);

  if (!hasInfoBanner && !hasImpersonationBanner) {
    return null;
  }

  return (
    <>
      {hasImpersonationBanner && (
        <BannerContainer className="warning" style={{ justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
          <BannerText style={{ textAlign: 'left', margin: 0, padding: 0 }}>
            Вы вошли как пользователь {impersonationInfo.userEmail || 'неизвестный пользователь'}.
            {impersonationInfo.impersonatorEmail ? ` Администратор: ${impersonationInfo.impersonatorEmail}.` : ''}
          </BannerText>
          <button
            type="button"
            onClick={handleRestoreAdmin}
            disabled={restoring}
            style={{
              border: '1px solid rgba(255, 255, 255, 0.35)',
              background: 'rgba(255, 255, 255, 0.15)',
              color: 'white',
              padding: '8px 14px',
              borderRadius: '8px',
              fontWeight: 600,
              cursor: restoring ? 'wait' : 'pointer'
            }}
          >
            {restoring ? 'Возвращение...' : 'Вернуться в админку'}
          </button>
        </BannerContainer>
      )}
      {hasInfoBanner && (
        <BannerContainer className={banner.banner_type || 'info'}>
          <BannerText>{banner.text}</BannerText>
          <CloseButton
            type="button"
            onClick={handleDismissBanner}
            aria-label="Скрыть баннер"
            title="Скрыть баннер"
          >
            ×
          </CloseButton>
        </BannerContainer>
      )}
    </>
  );
};

export default InfoBanner;

